import { all, call, delay, put, takeLatest } from "redux-saga/effects";
import { AUTH_CHECK, CONNECTION_TYPE, PROMPT_TYPE, SIGN_UP_TAB_VALUE, TOAST_TYPES } from "enum/enums";
import { getLocalStorageData, handleErrorMessage } from "lib/utils";
import { setAuth, setCustomerId, setIsAppInstalled, setLoading, setLoginLoading, setProfileInfo, setStep } from "./reducer";
import { HANDLE_APP_INSTALL_CHECK, HANDLE_AUTH, HANDLE_CHECK_SESSION, HANDLE_TOKEN, HANDLE_UPDATE_USER_REGISTER, HANDLE_USER_LOGIN, HANDLE_USER_REGISTER } from "./action.types";
import { checkAppInstall, getUserDetails, registerUser, UpdateRegisterUser } from "services/authServices";
import { JWT_TOKEN } from "lib/Constants";
import { addToast } from "store/toaster/reducer";
import { UserFormData } from "interface/interface";
import { validateUserOnAppLoad } from "services/authSession";
import { auth0Service } from "services/auth0Service";


function* handleAuth() {
	try {
		yield all([call([auth0Service, auth0Service.logout], window.location.origin), call(() => localStorage.clear())]);
	} catch (error) {
		console.error("Error handling auth:", error);
	}
}
function* handleToken(action: { type: string; payload: boolean }): Generator {
	if (!action.payload) setLoading(true);
	const details = (yield call(handleCheckSession)) as AUTH_CHECK | null;
	if (details && !details?.needsLogin) {
		yield put(setAuth(true));
		setAuth(true);
		try {
			const res: any = yield call(getUserDetails);
			const data = res?.data;
			yield put(setProfileInfo(data));
		} catch (error) {
			console.log(error);
		}
		yield put(setLoading(false));
		return true;
	} else {
		yield put(setAuth(false));
		const hasToken = getLocalStorageData(JWT_TOKEN);
		if (hasToken) {
			yield call(handleAuth);
		}
		yield put(setLoading(false));
		return false;
	}
}

function* handleCheckSession(): Generator {
	try {
		const authCheck = yield call(validateUserOnAppLoad);
		return authCheck;
	} catch (err) {
		console.error("Auth callback error:", err);
		return null;
	}
}

function* handleUserLogin(action: { type: string; payload: { email: string; promptType?: PROMPT_TYPE; connection: CONNECTION_TYPE; inviteCode?: string } }): Generator {
	yield put(setLoginLoading(true));
	try {
		const { email, inviteCode, promptType, connection } = action.payload;
		yield auth0Service.loginWithRedirect({ email, inviteCode, promptType, connection });
	} catch (error) {
		yield put(addToast({ message: handleErrorMessage(error), type: TOAST_TYPES.ERROR }));
	} finally {
		yield put(setLoginLoading(false));
	}
}

function* handleUserRegister(action: { type: string; payload: UserFormData }): Generator {
	yield put(setLoginLoading(true));
	const { step, ...rest } = action.payload;
	try {
		const res: any = yield call(registerUser, rest);
		yield put(setCustomerId(res?.data?.customerId));
		yield put(setStep(step));
	} catch (error) {
		yield put(addToast({ message: handleErrorMessage(error), type: TOAST_TYPES.ERROR }));
	} finally {
		yield put(setLoginLoading(false));
	}
}

function* handleUpdateRegisterUser(action: { type: string; payload: UserFormData }): Generator {
	yield put(setLoginLoading(true));
	try {
		const { step, ...rest } = action.payload;
		yield call(UpdateRegisterUser, rest);
		yield put(setStep(step));
	} catch (error) {
		yield put(addToast({ message: handleErrorMessage(error), type: TOAST_TYPES.ERROR }));
	} finally {
		yield put(setLoginLoading(false));
	}
}

function* handleAppInstallCheck(action: { type: string; payload: string }): Generator {
	yield put(setLoginLoading(true));
	yield put(setIsAppInstalled(false));
	try {
		let maxAttempts = 20;
		while (maxAttempts > 1) {
			try {
				const res: any = yield call(checkAppInstall, action.payload);
				const appInstalled = res?.data?.appInstalled;
				const subscriptionValid = res?.data?.subscriptionValid;
				if (subscriptionValid && appInstalled) {
					yield put(setStep(SIGN_UP_TAB_VALUE.FOUR));
					break;
				} else if (appInstalled) {
					yield put(setIsAppInstalled(true));
				}
				yield delay(5 * 1000);
				maxAttempts--;
			} catch (error) {
				console.log(error, "polling for app install check");
				break;
			}
		}
	} finally {
		yield put(setLoginLoading(false));
	}
}

function* authSaga() {
	yield takeLatest(HANDLE_TOKEN, handleToken);
	yield takeLatest(HANDLE_AUTH, handleAuth);
	yield takeLatest(HANDLE_USER_LOGIN, handleUserLogin);
	yield takeLatest(HANDLE_USER_REGISTER, handleUserRegister);
	yield takeLatest(HANDLE_APP_INSTALL_CHECK, handleAppInstallCheck);
	yield takeLatest(HANDLE_UPDATE_USER_REGISTER, handleUpdateRegisterUser);
	yield takeLatest(HANDLE_CHECK_SESSION, handleCheckSession);
}
export default authSaga;
